/**
 * Polygons clicked onto a splat surface: the plane they lie on, their outline in that plane,
 * and their area. Shared by the area measurement tool and annotation layer shapes, in the
 * editor and the runtime, so both always agree on the number shown.
 *
 * Points come from surface picks, so they are never exactly coplanar. The plane is the one
 * Newell's method gives (the polygon's own area vector), which is exact for planar outlines
 * and well-behaved for slightly bumpy ones. `maxDeviation` says how far the picks stray from
 * it; a large value means the outline wraps around a corner (two roof slopes, a wall and its
 * return) and the projected area is not the surface area, so callers warn about it.
 */
import { polygonSelfIntersects, type Polygon2D } from './polygonMesh';

export interface SurfacePoint {
    x: number;
    y: number;
    z: number;
}

export interface SurfacePolygon {
    /** Centroid of the points; origin of the 2D frame. */
    origin: SurfacePoint;
    /** In-plane axes and plane normal, unit length and mutually perpendicular. */
    u: SurfacePoint;
    v: SurfacePoint;
    normal: SurfacePoint;
    /** The points projected into the plane, relative to `origin`, in the input order. */
    outline: Polygon2D;
    /** Area of the projected outline, in scene units squared. */
    area: number;
    /** Farthest distance of any point from the plane, in scene units. */
    maxDeviation: number;
    /** Diagonal of the outline's 2D bounding box, in scene units. */
    extent: number;
    /** False when the outline is too far from flat for its area to be trusted. */
    planar: boolean;
    /** True when edges of the outline cross each other (the area is then meaningless). */
    selfIntersecting: boolean;
}

/** Points may stray from the plane by this fraction of the outline's extent and still count as flat. */
export const PLANAR_TOLERANCE = 0.08;

const finite = (p: SurfacePoint | null | undefined): p is SurfacePoint =>
    !!p && Number.isFinite(p.x) && Number.isFinite(p.y) && Number.isFinite(p.z);

const sub = (a: SurfacePoint, b: SurfacePoint): SurfacePoint => ({ x: a.x - b.x, y: a.y - b.y, z: a.z - b.z });
const dot = (a: SurfacePoint, b: SurfacePoint): number => a.x * b.x + a.y * b.y + a.z * b.z;
const cross = (a: SurfacePoint, b: SurfacePoint): SurfacePoint => ({
    x: a.y * b.z - a.z * b.y,
    y: a.z * b.x - a.x * b.z,
    z: a.x * b.y - a.y * b.x,
});
const length = (a: SurfacePoint): number => Math.sqrt(dot(a, a));
const scale = (a: SurfacePoint, s: number): SurfacePoint => ({ x: a.x * s, y: a.y * s, z: a.z * s });

/** Drop non-finite points and consecutive duplicates (a double click lands twice), and a closing repeat of the first. */
export function cleanSurfacePoints(points: ReadonlyArray<SurfacePoint | null | undefined> | null | undefined): SurfacePoint[] {
    const out: SurfacePoint[] = [];
    if (!Array.isArray(points)) return out;
    for (const p of points) {
        if (!finite(p)) continue;
        const prev = out[out.length - 1];
        if (prev && length(sub(prev, p)) <= 1e-9) continue;
        out.push({ x: p.x, y: p.y, z: p.z });
    }
    if (out.length > 1 && length(sub(out[0], out[out.length - 1])) <= 1e-9) out.pop();
    return out;
}

/**
 * Fit a plane to the outline and project it. Null for fewer than three distinct points or a
 * degenerate outline (all points on one line).
 */
export function fitSurfacePolygon(input: ReadonlyArray<SurfacePoint | null | undefined> | null | undefined): SurfacePolygon | null {
    const points = cleanSurfacePoints(input);
    if (points.length < 3) return null;
    const origin = scale(
        points.reduce((acc, p) => ({ x: acc.x + p.x, y: acc.y + p.y, z: acc.z + p.z }), { x: 0, y: 0, z: 0 }),
        1 / points.length,
    );
    // Newell's method: twice the polygon's vector area.
    const newell = { x: 0, y: 0, z: 0 };
    for (let i = 0; i < points.length; i++) {
        const a = sub(points[i], origin);
        const b = sub(points[(i + 1) % points.length], origin);
        const c = cross(a, b);
        newell.x += c.x;
        newell.y += c.y;
        newell.z += c.z;
    }
    const newellLength = length(newell);
    let span = 0;
    for (const p of points) span = Math.max(span, length(sub(p, origin)));
    if (!(span > 0) || newellLength <= 1e-9 * span * span) return null;
    const normal = scale(newell, 1 / newellLength);
    // Keep the outline upright on walls: `v` follows scene up projected into the plane. On
    // floors and ceilings (normal close to up) fall back to the scene Z axis.
    const up = Math.abs(normal.y) < 0.95 ? { x: 0, y: 1, z: 0 } : { x: 0, y: 0, z: 1 };
    const uRaw = cross(up, normal);
    const u = scale(uRaw, 1 / length(uRaw));
    const v = cross(normal, u);
    const outline: Polygon2D = [];
    let maxDeviation = 0;
    let minU = Infinity, maxU = -Infinity, minV = Infinity, maxV = -Infinity;
    for (const p of points) {
        const d = sub(p, origin);
        const pu = dot(d, u);
        const pv = dot(d, v);
        outline.push({ u: pu, v: pv });
        maxDeviation = Math.max(maxDeviation, Math.abs(dot(d, normal)));
        minU = Math.min(minU, pu);
        maxU = Math.max(maxU, pu);
        minV = Math.min(minV, pv);
        maxV = Math.max(maxV, pv);
    }
    let twiceArea = 0;
    for (let i = 0; i < outline.length; i++) {
        const a = outline[i];
        const b = outline[(i + 1) % outline.length];
        twiceArea += a.u * b.v - b.u * a.v;
    }
    const extent = Math.hypot(maxU - minU, maxV - minV);
    return {
        origin,
        u,
        v,
        normal,
        outline,
        area: Math.abs(twiceArea) / 2,
        maxDeviation,
        extent,
        planar: maxDeviation <= PLANAR_TOLERANCE * extent,
        selfIntersecting: polygonSelfIntersects(outline),
    };
}

/** A surface fit that also says which picks are off the surface the others lie on. */
export interface SurfaceOutlineFit extends SurfacePolygon {
    /**
     * Indices (into the cleaned points) of picks far off the surface the rest lie on, e.g. a
     * click that landed on a tree or a floater above a lawn. They still shape the outline (its
     * footprint), but neither tilt the plane nor count in `maxDeviation`.
     */
    offSurface: number[];
}

/** A pick is off the surface when it is this many times farther from the plane than the typical pick… */
export const OFF_SURFACE_MEDIAN_FACTOR = 4;
/** …and farther than this fraction of the outline's extent (gentle bumps of a lawn or a wall stay in). */
export const OFF_SURFACE_EXTENT_FRACTION = 0.04;

const median = (values: number[]): number => {
    const sorted = [...values].sort((a, b) => a - b);
    const mid = sorted.length >> 1;
    return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
};

/** `plane`'s frame applied to every point (the off-surface ones shape the outline too). */
function projectOntoPlane(points: SurfacePoint[], plane: SurfacePolygon, offSurface: ReadonlySet<number>): SurfacePolygon {
    const { origin, u, v, normal } = plane;
    const outline: Polygon2D = [];
    let maxDeviation = 0;
    let minU = Infinity, maxU = -Infinity, minV = Infinity, maxV = -Infinity;
    points.forEach((p, i) => {
        const d = sub(p, origin);
        const pu = dot(d, u);
        const pv = dot(d, v);
        outline.push({ u: pu, v: pv });
        if (!offSurface.has(i)) maxDeviation = Math.max(maxDeviation, Math.abs(dot(d, normal)));
        minU = Math.min(minU, pu);
        maxU = Math.max(maxU, pu);
        minV = Math.min(minV, pv);
        maxV = Math.max(maxV, pv);
    });
    let twiceArea = 0;
    for (let i = 0; i < outline.length; i++) {
        const a = outline[i];
        const b = outline[(i + 1) % outline.length];
        twiceArea += a.u * b.v - b.u * a.v;
    }
    const extent = Math.hypot(maxU - minU, maxV - minV);
    return {
        origin,
        u,
        v,
        normal,
        outline,
        area: Math.abs(twiceArea) / 2,
        maxDeviation,
        extent,
        planar: maxDeviation <= PLANAR_TOLERANCE * extent,
        selfIntersecting: polygonSelfIntersects(outline),
    };
}

/**
 * Fit an outline drawn by clicking on a scan, where a click can miss the surface and land on
 * something in front of it (a tree over a lawn, a floater). Clicks far off the surface the others
 * lie on are set aside: the plane and `maxDeviation` come from the rest, while every click still
 * shapes the outline. Without such clicks it is exactly fitSurfacePolygon.
 *
 * The surface is the plane most clicks lie closest to (least median distance) among the plane of
 * all clicks and the planes through evenly spread triples of them, so a couple of stray clicks
 * can't tilt it. A click is off it when it is OFF_SURFACE_MEDIAN_FACTOR times farther than the
 * typical click and farther than OFF_SURFACE_EXTENT_FRACTION of the extent. When more than a
 * quarter of the clicks are off, the outline isn't a surface with a few stray clicks but really
 * wraps a corner (two roof slopes), and nothing is set aside.
 *
 * Regions' outline slabs use it (a stray click used to tilt the slab and make it metres thick);
 * area measurements keep fitSurfacePolygon, whose numbers visitors already see.
 */
export function fitSurfaceOutline(input: ReadonlyArray<SurfacePoint | null | undefined> | null | undefined): SurfaceOutlineFit | null {
    const points = cleanSurfacePoints(input);
    const all = fitSurfacePolygon(points);
    if (!all) return null;
    const most = Math.floor(points.length / 4);
    if (most === 0) return { ...all, offSurface: [] };
    const distances = (origin: SurfacePoint, normal: SurfacePoint) => points.map((p) => Math.abs(dot(sub(p, origin), normal)));
    /** Clicks off the plane (indices, ascending), or null when too many are: a corner, not stray clicks. */
    const offPlane = (deviations: number[], typical: number): number[] | null => {
        const limit = Math.max(OFF_SURFACE_MEDIAN_FACTOR * typical, OFF_SURFACE_EXTENT_FRACTION * all.extent);
        const off = deviations.flatMap((d, i) => (d > limit ? [i] : []));
        return off.length > most ? null : off;
    };
    const n = points.length;
    const step1 = Math.floor(n / 3);
    const step2 = Math.floor((2 * n) / 3);
    let best = { deviations: distances(all.origin, all.normal), typical: 0 };
    best.typical = median(best.deviations);
    for (let i = 0; i < n; i++) {
        const p = points[i];
        const c = cross(sub(points[(i + step1) % n], p), sub(points[(i + step2) % n], p));
        const size = length(c);
        if (size <= 1e-6 * all.extent * all.extent) continue;
        const deviations = distances(p, scale(c, 1 / size));
        const typical = median(deviations);
        if (typical < best.typical) best = { deviations, typical };
    }
    const off = offPlane(best.deviations, best.typical);
    if (!off?.length) return { ...all, offSurface: [] };
    const plane = fitSurfacePolygon(points.filter((_, i) => !off.includes(i)));
    if (!plane) return { ...all, offSurface: [] };
    // Settle against the plane of the clicks kept (a click that was only off the robust plane comes back).
    const settled = distances(plane.origin, plane.normal);
    const final = offPlane(settled, median(settled.filter((_, i) => !off.includes(i))));
    if (!final?.length) return { ...all, offSurface: [] };
    const surface = final.length === off.length && final.every((i, k) => i === off[k]) ? plane : fitSurfacePolygon(points.filter((_, i) => !final.includes(i)));
    if (!surface) return { ...all, offSurface: [] };
    return { ...projectOntoPlane(points, surface, new Set(final)), offSurface: final };
}

/** Area of a surface outline in scene units squared; 0 when it can't be fitted. */
export function surfacePolygonArea(points: ReadonlyArray<SurfacePoint | null | undefined> | null | undefined): number {
    return fitSurfacePolygon(points)?.area ?? 0;
}

export type MeasurementUnit = 'meters' | 'centimeters' | 'feet' | 'inches';

/** Display units per metre-equivalent scene unit (after the scene's calibration scale). */
export const MEASUREMENT_UNIT_MULTIPLIERS: Record<MeasurementUnit, number> = {
    meters: 1,
    centimeters: 100,
    feet: 3.28084,
    inches: 39.3701,
};
export const MEASUREMENT_UNIT_ABBREVIATIONS: Record<MeasurementUnit, string> = {
    meters: 'm',
    centimeters: 'cm',
    feet: 'ft',
    inches: 'in',
};

const unitOf = (unit: string | undefined): MeasurementUnit =>
    unit && unit in MEASUREMENT_UNIT_MULTIPLIERS ? (unit as MeasurementUnit) : 'meters';

/** Scene-unit area converted with the calibration scale (a length factor, so it applies squared). */
export function convertArea(rawArea: number, sceneScale: number | undefined, unit: string | undefined): number {
    const s = typeof sceneScale === 'number' && Number.isFinite(sceneScale) && sceneScale > 0 ? sceneScale : 1;
    const m = MEASUREMENT_UNIT_MULTIPLIERS[unitOf(unit)];
    return rawArea * s * s * m * m;
}

/** Scene-unit distance converted with the calibration scale into the display unit. */
export function convertDistance(rawDistance: number, sceneScale: number | undefined, unit: string | undefined): number {
    const s = typeof sceneScale === 'number' && Number.isFinite(sceneScale) && sceneScale > 0 ? sceneScale : 1;
    return rawDistance * s * MEASUREMENT_UNIT_MULTIPLIERS[unitOf(unit)];
}

const numberFormats = new Map<string, Intl.NumberFormat>();

/**
 * A measurement number with fixed decimals and the grouping and decimal marks of `locale`
 * ("57,656" in English, "57 656" in French, "57.656" in German). `locale` defaults to the
 * visitor's browser language; an unknown locale falls back to the browser's default.
 */
export function formatMeasurementNumber(value: number, decimals: number, locale?: string): string {
    const key = `${locale ?? ''}|${decimals}`;
    let format = numberFormats.get(key);
    if (!format) {
        const options = { minimumFractionDigits: decimals, maximumFractionDigits: decimals };
        try {
            format = new Intl.NumberFormat(locale, options);
        } catch {
            format = new Intl.NumberFormat(undefined, options);
        }
        numberFormats.set(key, format);
    }
    return format.format(value);
}

/**
 * "1,234.56 ft" — the distance label the viewer's measurement labels and list and the editor share:
 * two decimals, grouped in the visitor's language (see formatMeasurementNumber).
 */
export function formatDistance(rawDistance: number, sceneScale: number | undefined, unit: string | undefined, locale?: string): string {
    const value = convertDistance(rawDistance, sceneScale, unit);
    return `${formatMeasurementNumber(value, 2, locale)} ${MEASUREMENT_UNIT_ABBREVIATIONS[unitOf(unit)]}`;
}

/**
 * "57,656 ft²" — the label the measurement list, the on-splat label and the layers panel share:
 * no decimals from 1,000 up, one from 100, two below, grouped in the visitor's language.
 */
export function formatArea(rawArea: number, sceneScale: number | undefined, unit: string | undefined, locale?: string): string {
    const value = convertArea(rawArea, sceneScale, unit);
    const decimals = value >= 1000 ? 0 : value >= 100 ? 1 : 2;
    return `${formatMeasurementNumber(value, decimals, locale)} ${MEASUREMENT_UNIT_ABBREVIATIONS[unitOf(unit)]}²`;
}
